import { Injectable } from '@angular/core';

export type SymptomClassification = 'normal' | 'mildToModerate' | 'severe';
export type DiscordanceType = 'painWithoutStain' | 'stainWithoutPain' | 'neither' | null;
export type HomeostasisCategory = 'tearFilmStability' | 'osmolarity' | 'ocularSurfaceStaining';

export interface HomeostasisResult {
  category: HomeostasisCategory;
  performed: boolean;
  positive: boolean;
}

export interface DiagnosisResult {
  dedDiagnosed: boolean;
  discordanceType: DiscordanceType;
  symptomClassification: SymptomClassification;
  hasPositiveMarker: boolean;
  categoriesTested: number;
  isInsufficientToRuleOut: boolean;
}

export const HOMEOSTASIS_CATEGORY_LABELS: { [K in HomeostasisCategory]: { code: string; label: string } } = {
  tearFilmStability:     { code: '3A', label: 'Tear Film Stability' },
  osmolarity:            { code: '3B', label: 'Tear Osmolarity' },
  ocularSurfaceStaining: { code: '3C', label: 'Ocular Surface Staining' },
};

export function discordanceTypeLabel(discordanceType: string | null): string {
  switch (discordanceType) {
    case 'painWithoutStain': return 'Symptoms Without Signs';
    case 'stainWithoutPain': return 'Signs Without Symptoms';
    case 'neither':          return 'Neither Symptoms Nor Signs';
    default:                 return '';
  }
}

@Injectable()
export class DewsIIIService {

  classifyOsdiScore(score: number): SymptomClassification {
    if (score === null || score === undefined || score < 0) {
      return 'normal';
    }
    if (score <= 3) {
      return 'normal';
    }
    if (score <= 8) {
      return 'mildToModerate';
    }
    return 'severe';
  }

  hasSymptoms(score: number): boolean {
    return score !== null && score !== undefined && score >= 4;
  }

  evaluateNibut(value: number): boolean {
    return value !== null && value !== undefined && value < 10;
  }

  evaluateFbut(value: number): boolean {
    return value !== null && value !== undefined && value < 5;
  }

  evaluateOsmolarity(higherEye: number, lowerEye: number): boolean {
    if (higherEye === null || higherEye === undefined) {
      return false;
    }
    if (higherEye >= 308) {
      return true;
    }
    if (lowerEye !== null && lowerEye !== undefined) {
      return Math.abs(higherEye - lowerEye) > 8;
    }
    return false;
  }

  evaluateCornealStaining(punctateSpots: number): boolean {
    return punctateSpots !== null && punctateSpots !== undefined && punctateSpots > 5;
  }

  evaluateConjunctivalStaining(punctateSpots: number): boolean {
    return punctateSpots !== null && punctateSpots !== undefined && punctateSpots > 9;
  }

  evaluateLidMarginStaining(lengthMm: number, widthPercent: number): boolean {
    return lengthMm !== null && lengthMm !== undefined &&
      widthPercent !== null && widthPercent !== undefined &&
      lengthMm >= 2 && widthPercent >= 25;
  }

  hasPositiveHomeostasisMarker(results: HomeostasisResult[]): boolean {
    return results.some(r => r.performed && r.positive);
  }

  getTestCategoriesPerformed(results: HomeostasisResult[]): number {
    return results.filter(r => r.performed).length;
  }

  isInsufficientToRuleOut(results: HomeostasisResult[]): boolean {
    const performed = results.filter(r => r.performed);
    if (performed.length < 2) {
      return performed.every(r => !r.positive);
    }
    return false;
  }

  determineDiagnosis(osdiScore: number, results: HomeostasisResult[]): DiagnosisResult {
    const symptomClassification = this.classifyOsdiScore(osdiScore);
    const symptomsPresent = this.hasSymptoms(osdiScore);
    const hasPositiveMarker = this.hasPositiveHomeostasisMarker(results);
    const categoriesTested = this.getTestCategoriesPerformed(results);
    const insufficientToRuleOut = this.isInsufficientToRuleOut(results);

    const dedDiagnosed = symptomsPresent && hasPositiveMarker;
    let discordanceType: DiscordanceType = null;

    if (!dedDiagnosed) {
      if (symptomsPresent && !hasPositiveMarker && categoriesTested >= 2) {
        discordanceType = 'painWithoutStain';
      } else if (!symptomsPresent && hasPositiveMarker) {
        discordanceType = 'stainWithoutPain';
      } else if (!symptomsPresent && !hasPositiveMarker && categoriesTested >= 2) {
        discordanceType = 'neither';
      }
    }

    return {
      dedDiagnosed,
      discordanceType,
      symptomClassification,
      hasPositiveMarker,
      categoriesTested,
      isInsufficientToRuleOut: insufficientToRuleOut
    };
  }

  diagnosisBannerClass(result: DiagnosisResult): string {
    if (!result) {
      return 'banner-unknown';
    }
    if (result.dedDiagnosed) {
      return 'banner-diagnosed';
    }
    if (result.discordanceType) {
      return 'banner-discordance';
    }
    if (result.categoriesTested < 2) {
      return 'banner-unknown';
    }
    return 'banner-ruled-out';
  }

  diagnosisLabel(result: DiagnosisResult, osdiScore: number): string {
    if (!result) {
      return 'Awaiting Data';
    }
    if (
      result.categoriesTested < 1 &&
      (osdiScore === null || osdiScore === undefined)
    ) {
      return 'Awaiting Data';
    }
    if (result.dedDiagnosed) {
      return 'DED Diagnosed';
    }
    if (result.discordanceType === 'painWithoutStain') {
      return 'Discordance: Symptoms Without Signs';
    }
    if (result.discordanceType === 'stainWithoutPain') {
      return 'Discordance: Signs Without Symptoms';
    }
    if (result.discordanceType === 'neither') {
      return 'DED Ruled Out';
    }
    if (result.isInsufficientToRuleOut) {
      return 'Insufficient Testing';
    }
    return 'DED Ruled Out';
  }

  symptomClassificationLabel(osdiScore: number): string {
    if (osdiScore === null || osdiScore === undefined) {
      return 'No OSDI-6 Score';
    }
    switch (this.classifyOsdiScore(osdiScore)) {
      case 'normal':
        return 'Normal (OSDI-6 ≤ 3)';
      case 'mildToModerate':
        return 'Mild to Moderate (OSDI-6 4–8)';
      case 'severe':
        return 'Severe (OSDI-6 > 8)';
      default:
        return 'Unknown';
    }
  }

  discordanceGuidance(result: DiagnosisResult): string {
    if (!result || !result.discordanceType) {
      return '';
    }
    if (result.discordanceType === 'painWithoutStain') {
      return 'Consider neuropathic pain assessment. Symptoms present without objective homeostasis marker disruption may indicate neuropathic etiology.';
    }
    if (result.discordanceType === 'stainWithoutPain') {
      return 'Consider neurotrophic assessment. Positive homeostasis markers without symptoms may indicate reduced corneal sensitivity.';
    }
    return '';
  }
}
